import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Send,
  Loader2,
  CheckCircle2,
  Truck,
  Package,
  Warehouse,
  Calculator,
  MapPin,
  Clock,
  Euro,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import CityAutocomplete from "../components/CityAutocomplete";
import RouteMap from "../components/RouteMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  { value: "trasporto", label: "Trasporto e Spedizioni", icon: Truck },
  { value: "magazzino", label: "Magazzino e Stoccaggio", icon: Warehouse },
  { value: "dedicato", label: "Servizio Dedicato", icon: Truck },
];

export default function QuotePage() {
  const [form, setForm] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    service_type: "",
    origin: "Castiglione delle Stiviere",
    destination: "",
    weight: "",
    dimensions: "",
    numero_colli: "",
    tipo_imballo: "",
    description: "",
    urgency: "normale",
  });
  const [items, setItems] = useState([{ type: "scatola", quantity: 1, length: "", width: "", height: "" }]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    axios.get(`${API}/cities`).then((res) => setCities(res.data)).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (estimate) setEstimate(null);
  };

  const updateItem = (i, field, value) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: field === "quantity" ? Math.max(1, parseInt(value, 10) || 1) : value };
    setItems(next);
    if (estimate) setEstimate(null);
  };
  const addItem = () => setItems([...items, { type: "scatola", quantity: 1, length: "", width: "", height: "" }]);
  const removeItem = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);

  const handleEstimate = async () => {
    if (!form.destination) {
      toast.error("Inserisci la citta di destinazione");
      return;
    }
    if (!items.some(it => it.type && it.quantity > 0)) {
      toast.error("Aggiungi almeno un collo");
      return;
    }
    setEstimateLoading(true);
    try {
      const res = await axios.post(`${API}/estimate`, {
        origin: "Castiglione delle Stiviere",
        destination: form.destination,
        items: items.filter(it => it.type && it.quantity > 0),
        urgency: "normale",
        service_type: "trasporto",
      });
      setEstimate(res.data);
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Impossibile calcolare. Verifica la citta inserita."
      );
    } finally {
      setEstimateLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      // Compose description with items detail + dimensions
      const itemsDesc = items.map(it => {
        const dims = [it.length, it.width, it.height].filter(Boolean);
        const dimsStr = dims.length === 3 ? ` (${dims.join("×")}cm)` : "";
        return `${it.quantity}x ${it.type}${dimsStr}`;
      }).join(", ");
      await axios.post(`${API}/quotes`, {
        ...form,
        numero_colli: items.reduce((sum, it) => sum + it.quantity, 0).toString(),
        tipo_imballo: items[0]?.type || "scatola",
        description: form.description ? `${form.description}\n\nColli: ${itemsDesc}` : `Colli: ${itemsDesc}`,
      });
      toast.success("Richiesta preventivo inviata con successo!");
      setSubmitted(true);
    } catch {
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main>
        <section className="bg-[#09090B] pt-28 pb-20 md:pt-36 md:pb-28">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-4 font-heading">
              Preventivo
            </p>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white tracking-tight uppercase">
              Richiesta Inviata
            </h1>
          </div>
        </section>
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-2xl mx-auto px-4 text-center" data-testid="quote-success">
            <CheckCircle2 size={64} className="text-[#FF5200] mx-auto mb-6" />
            <h2 className="font-heading text-3xl font-bold uppercase mb-4 text-[#09090B]">
              Grazie per la tua richiesta!
            </h2>
            <p className="text-zinc-600 text-lg mb-8 font-body">
              Abbiamo ricevuto la tua richiesta di preventivo. Il nostro team ti
              contattera entro 24 ore lavorative con una proposta personalizzata.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setEstimate(null);
                setForm({
                  company: "",
                  name: "",
                  email: "",
                  phone: "",
                  service_type: "",
                  origin: "",
                  destination: "",
                  weight: "",
                  dimensions: "",
                  numero_colli: "",
                  tipo_imballo: "",
                  description: "",
                  urgency: "normale",
                });
              }}
              data-testid="quote-new-request-btn"
              className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm"
            >
              Nuova Richiesta
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      {/* Hero */}
      <section className="bg-[#09090B] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-4 font-heading">
              Preventivo
            </p>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight uppercase leading-[0.9]">
              Richiedi un
              <br />
              <span className="text-[#FF5200]">preventivo gratuito</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 md:py-28 bg-white" data-testid="quote-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                {/* Service Type */}
                <div>
                  <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-3 font-heading">
                    Tipo di Servizio *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {serviceOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setForm({ ...form, service_type: opt.value });
                          if (estimate) setEstimate(null);
                        }}
                        data-testid={`quote-service-${opt.value}`}
                        className={`flex flex-col items-center gap-2 p-4 border-2 rounded-sm transition-colors duration-200 ${
                          form.service_type === opt.value
                            ? "border-[#FF5200] bg-[#FF5200]/5"
                            : "border-zinc-200 hover:border-zinc-400"
                        }`}
                      >
                        <opt.icon
                          size={28}
                          className={
                            form.service_type === opt.value
                              ? "text-[#FF5200]"
                              : "text-zinc-400"
                          }
                          strokeWidth={1.5}
                        />
                        <span
                          className={`text-xs font-bold tracking-wide uppercase font-heading ${
                            form.service_type === opt.value
                              ? "text-[#FF5200]"
                              : "text-zinc-500"
                          }`}
                        >
                          {opt.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Shipment Details */}
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4">
                  <p className="text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading mb-2">Punto di partenza</p>
                  <p className="text-sm font-bold text-[#09090B] flex items-center gap-2"><MapPin size={16} className="text-[#FF5200]" /> Castiglione delle Stiviere (MN)</p>
                </div>

                <div>
                  <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                    Destinazione *
                  </label>
                  <CityAutocomplete
                    value={form.destination}
                    onChange={(val) => {
                      setForm((f) => ({ ...f, destination: val }));
                      if (estimate) setEstimate(null);
                    }}
                    cities={cities}
                    placeholder="Cerca citta di destinazione..."
                    testId="quote-destination-input"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold tracking-widest uppercase text-zinc-500 font-heading">
                      Colli *
                    </label>
                    <button type="button" onClick={addItem} data-testid="quote-add-item-btn"
                      className="text-xs text-[#FF5200] font-bold flex items-center gap-1 uppercase tracking-wide">
                      + Aggiungi
                    </button>
                  </div>
                  <div className="space-y-3">
                    {items.map((item, i) => (
                      <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <select value={item.type} onChange={e => updateItem(i, "type", e.target.value)}
                            data-testid={`quote-item-type-${i}`}
                            className="flex-1 h-10 px-3 border border-zinc-300 rounded-sm bg-white text-sm font-body focus:border-[#FF5200] outline-none">
                            <option value="scatola">Scatola</option>
                            <option value="bancale">Bancale</option>
                          </select>
                          <input type="number" min="1" value={item.quantity}
                            onChange={e => updateItem(i, "quantity", e.target.value)}
                            placeholder="Qta"
                            data-testid={`quote-item-qty-${i}`}
                            className="w-20 h-10 px-2 border border-zinc-300 rounded-sm text-center text-sm font-bold" />
                          {items.length > 1 && (
                            <button type="button" onClick={() => removeItem(i)}
                              className="p-2 text-zinc-400 hover:text-red-500"
                              aria-label="Rimuovi collo">×</button>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <input type="number" min="0" step="0.1" value={item.length || ""}
                            onChange={e => updateItem(i, "length", e.target.value)}
                            placeholder="L (cm)"
                            data-testid={`quote-item-length-${i}`}
                            className="h-9 px-2 border border-zinc-300 rounded-sm bg-white text-xs text-center focus:border-[#FF5200] outline-none" />
                          <input type="number" min="0" step="0.1" value={item.width || ""}
                            onChange={e => updateItem(i, "width", e.target.value)}
                            placeholder="P (cm)"
                            data-testid={`quote-item-width-${i}`}
                            className="h-9 px-2 border border-zinc-300 rounded-sm bg-white text-xs text-center focus:border-[#FF5200] outline-none" />
                          <input type="number" min="0" step="0.1" value={item.height || ""}
                            onChange={e => updateItem(i, "height", e.target.value)}
                            placeholder="H (cm)"
                            data-testid={`quote-item-height-${i}`}
                            className="h-9 px-2 border border-zinc-300 rounded-sm bg-white text-xs text-center focus:border-[#FF5200] outline-none" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                      Peso indicativo (kg)
                    </label>
                    <input
                      type="text"
                      name="weight"
                      value={form.weight}
                      onChange={handleChange}
                      className="w-full h-12 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                      placeholder="es. 500"
                      data-testid="quote-weight-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                      Dimensioni (L × P × H cm)
                    </label>
                    <input
                      type="text"
                      name="dimensions"
                      value={form.dimensions}
                      onChange={handleChange}
                      className="w-full h-12 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                      placeholder="es. 120x80x60"
                      data-testid="quote-dimensions-input"
                    />
                  </div>
                </div>

                {/* Estimate Button */}
                <div>
                  <button
                    type="button"
                    onClick={handleEstimate}
                    disabled={estimateLoading}
                    className="inline-flex items-center gap-2 border-2 border-[#FF5200] text-[#FF5200] px-6 py-3 font-bold uppercase tracking-wide rounded-sm hover:bg-[#FF5200] hover:text-white transition-colors duration-200 font-heading text-sm disabled:opacity-50"
                    data-testid="quote-estimate-btn"
                  >
                    {estimateLoading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Calculator size={18} />
                    )}
                    Calcola Stima Preventivo
                  </button>
                </div>

                {/* Estimate Result */}
                {estimate && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-[#FFF7ED] border-2 border-[#FF5200] p-6"
                    data-testid="quote-estimate-result"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <Calculator size={22} className="text-[#FF5200]" />
                      <h3 className="font-heading text-lg font-bold uppercase text-[#09090B]">
                        Stima Preventivo
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-4">
                      <div className="flex items-start gap-3">
                        <Euro size={20} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-bold tracking-widest uppercase text-zinc-500 font-heading">
                            Prezzo Stimato
                          </p>
                          <p className="text-2xl font-bold text-[#09090B] font-heading">
                            {estimate.price_exact.toFixed(2)} &euro;
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <MapPin size={20} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-bold tracking-widest uppercase text-zinc-500 font-heading">
                            Distanza
                          </p>
                          <p className="text-2xl font-bold text-[#09090B] font-heading">
                            ~{estimate.distance_km.toFixed(0)} km
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Clock size={20} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-bold tracking-widest uppercase text-zinc-500 font-heading">
                            Tempi di Consegna
                          </p>
                          <p className="text-2xl font-bold text-[#09090B] font-heading">
                            {estimate.delivery_days_min}-{estimate.delivery_days_max} giorni
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border border-zinc-200 p-4 mb-3">
                      <p className="text-xs font-bold tracking-widest uppercase text-zinc-400 mb-2 font-heading">Dettaglio</p>
                      <div className="space-y-1 text-sm font-body text-zinc-600">
                        {estimate.zone && (
                          <p>Zona: <strong className="text-[#09090B]">{estimate.zone}</strong></p>
                        )}
                        {(estimate.items || []).map((it, idx) => (
                          <p key={idx}>{it.quantity} × {it.label} = <strong>{it.subtotal.toFixed(2)}€</strong></p>
                        ))}
                        <p>Totale colli: <strong className="text-[#09090B]">{estimate.package_cost?.toFixed(2)}€</strong></p>
                        {estimate.distance_cost > 0 && (
                          <p>Supplemento km (oltre 60km): <strong className="text-[#FF5200]">+{estimate.distance_cost.toFixed(2)}€</strong></p>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500 font-body">
                      * Stima indicativa. Il preventivo finale potrebbe variare in base a fattori specifici della spedizione.
                    </p>
                    {estimate.origin_coords && estimate.dest_coords && (
                      <div className="mt-4">
                        <RouteMap
                          originCoords={estimate.origin_coords}
                          destCoords={estimate.dest_coords}
                          originName={estimate.origin}
                          destName={estimate.destination}
                          distance={estimate.distance_km}
                        />
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Separator */}
                <div className="border-t-2 border-zinc-200 pt-6">
                  <p className="text-sm font-bold tracking-widest uppercase text-zinc-500 mb-4 font-heading">
                    Completa con i tuoi dati per ricevere il preventivo ufficiale
                  </p>
                </div>

                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                      Nome e Cognome *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                      placeholder="Mario Rossi"
                      data-testid="quote-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                      Azienda
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      className="w-full h-12 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                      placeholder="Nome azienda"
                      data-testid="quote-company-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                      placeholder="la.tua@email.com"
                      data-testid="quote-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                      Telefono *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                      placeholder="+39 XXX XXX XXXX"
                      data-testid="quote-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                    Descrizione merce
                  </label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none resize-none transition-colors duration-200 font-body"
                    placeholder="Descrivi il tipo di merce, dimensioni, eventuali esigenze speciali..."
                    data-testid="quote-description-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm disabled:opacity-50"
                  data-testid="quote-submit-btn"
                >
                  {loading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Send size={18} />
                  )}
                  {loading ? "Invio..." : "Invia Richiesta Preventivo"}
                </button>
              </form>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2">
              <div className="border-2 border-zinc-200 p-8 mb-8">
                <h3 className="font-heading text-xl font-bold uppercase mb-4 text-[#09090B]">
                  Perché richiedere un preventivo?
                </h3>
                <ul className="space-y-3">
                  {[
                    "Preventivo gratuito e senza impegno",
                    "Risposta entro 24 ore lavorative",
                    "Soluzioni personalizzate",
                    "Prezzi competitivi e trasparenti",
                    "Consulenza dedicata da esperti",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2
                        size={18}
                        className="text-[#FF5200] mt-0.5 flex-shrink-0"
                      />
                      <span className="text-zinc-600 font-body">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#09090B] p-8">
                <h3 className="font-heading text-xl font-bold uppercase mb-4 text-white">
                  Preferisci chiamare?
                </h3>
                <p className="text-zinc-400 mb-4 font-body">
                  Il nostro team è disponibile dal lunedì al venerdì, dalle 08:00
                  alle 18:00.
                </p>
                <a
                  href="tel:+393347708108"
                  className="text-[#FF5200] font-bold text-2xl font-heading"
                >
                  +39 334 770 8108
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
